import React from 'react';
import { Flame, DollarSign, Zap, Smile, MapPin } from 'lucide-react';

const WhyUs = () => {
  const reasons = [
    {
      icon: Flame,
      title: 'Freshly Grilled',
      description: 'Every dish cooked to order on our authentic charcoal grill',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: DollarSign,
      title: 'Unbeatable Prices',
      description: 'Quality meals from £4-£10, best value in London',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Zap,
      title: 'Fast Service',
      description: 'Hot food ready in under 10 minutes, even during rush',
      color: 'from-yellow-500 to-amber-600'
    },
    {
      icon: Smile,
      title: 'Friendly Staff',
      description: 'Family-owned with genuine warmth and hospitality',
      color: 'from-pink-500 to-rose-600'
    },
    {
      icon: MapPin,
      title: 'Prime Location',
      description: 'Heart of Chapel Market, easy to find and visit',
      color: 'from-blue-500 to-cyan-600'
    }
  ];

  return (
    <section className="py-20 bg-black relative overflow-hidden">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-orange-500 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-red-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Why People <span className="text-orange-500">Love Us</span>
          </h2>
          <div className="w-24 h-1 bg-orange-500 mx-auto mb-6" />
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            More than just great food - it's the complete experience that keeps customers coming back
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => (
            <div
              key={index}
              className="group relative bg-zinc-900/50 backdrop-blur-sm rounded-2xl p-8 border border-zinc-800 hover:border-orange-500/50 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl"
              style={{
                animationDelay: `${index * 100}ms`
              }}
            >
              {/* Hover Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 to-red-600/0 group-hover:from-orange-500/5 group-hover:to-red-600/5 rounded-2xl transition-all duration-300" />
              
              <div className="relative">
                {/* Icon */}
                <div className={`bg-gradient-to-br ${reason.color} w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                  <reason.icon className="h-8 w-8 text-white" />
                </div>
                
                {/* Content */}
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-orange-500 transition-colors duration-300">
                  {reason.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {reason.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Bar */}
        <div className="mt-16 bg-gradient-to-r from-orange-600/10 via-red-600/10 to-orange-600/10 rounded-2xl p-8 border border-orange-500/20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-orange-500 mb-2">231+</div>
              <div className="text-gray-400">Happy Reviews</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-orange-500 mb-2">£4-10</div>
              <div className="text-gray-400">Price Range</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-orange-500 mb-2">&lt;10min</div>
              <div className="text-gray-400">Service Time</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-orange-500 mb-2">4.0★</div>
              <div className="text-gray-400">Average Rating</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
